import {
	UNIDENTIFIED_SERVICE_WORKSPACE_ID,
	CANT_DELETE_BECAUSE_CONNECTIONS_KEY,
	UNIDENTIFIED_SERVICE_WORKSPACE_TITLE,
	CANT_DELETE_BECAUSE_SERVICE_WORKSPACE_KEY,
	TRANSLATED_UNIDENTIFIED_SERVICE_WORKSPACE_ID,
	TRANSLATED_UNIDENTIFIED_SERVICE_WORKSPACE_TITLE,
} from './create-and-manage.const';
import {
	PLACEMENT_MODE_NAME,
	PLACEMENT_ADD_ONS_MODE_NAME,
	PLACEMENT_MODE_EVERYTHING_NAME,
	PLACEMENT_FEATURE_TYPES_MODE_NAME,
} from '@/router/router.const.js';

// PRIVATE

const buildPlacementAddOnItem = (service, workspace, appWorkspace) => {
	if (service?.hidden) {
		return null;
	}
	return {
		slug: service?.service_slug,
		title: service?.service_name,
		workspaceId: workspace?.id || UNIDENTIFIED_SERVICE_WORKSPACE_ID,
		additionalInfo: getPlacementAccountWorkspaceAdditionalInfo(workspace, appWorkspace),
	};
};
const getPlacementAccountWorkspaceAdditionalInfo = (workspace, appWorkspace) =>
	!appWorkspace || !workspace
		? null
		: appWorkspace.workspaces.length <= 1 && workspace.id === UNIDENTIFIED_SERVICE_WORKSPACE_ID
		? null
		: workspace.title.replace(UNIDENTIFIED_SERVICE_WORKSPACE_TITLE, TRANSLATED_UNIDENTIFIED_SERVICE_WORKSPACE_TITLE);

const sortWorkspacesByUnidentifiedIsLast = (workspaces) =>
	workspaces.toSorted((a, b) => (a.id === UNIDENTIFIED_SERVICE_WORKSPACE_ID ? 1 : b.id === UNIDENTIFIED_SERVICE_WORKSPACE_ID ? -1 : 0));

const populateServiceDirectoryIdsWithProps = (arr, cantDeleteReasonKey, useDirectoryApp) =>
	arr.map((id) => {
		const directoryApp = useDirectoryApp(id);
		return { ...directoryApp, cantDeleteReason: cantDeleteReasonKey };
	});

const removePlacementAddOnsItemsDuplicates = (placementsAddOnsItems) =>
	removeDuplicateItemsInArrayByProperties(placementsAddOnsItems, ['slug', 'workspaceId']);

const formatAppsWorkspacesToPlacementsAddOns = (appsWorkspaces, getService) => {
	let appsWorkspacesAsPlacement = [];
	appsWorkspaces.forEach((appWorkspace) => {
		const service = getService(appWorkspace.id);
		const sortedWorkspaces = sortWorkspacesByUnidentifiedIsLast(appWorkspace.workspaces);
		sortedWorkspaces.forEach((workspace) => {
			const newMenuItem = buildPlacementAddOnItem(service, workspace, appWorkspace);
			appsWorkspacesAsPlacement = [...appsWorkspacesAsPlacement, newMenuItem];
		});
	});
	return appsWorkspacesAsPlacement;
};

const removeDuplicateItemsInArrayByProperties = (arr = [], uniquePropertyNames = []) =>
	arr.filter((v, i, a) => a.findIndex((v2) => uniquePropertyNames.every((k) => v2[k] === v[k])) === i);

const formatAllAccountConnectionsToPlacementAddOn = (allAccountConnections, getService) => {
	const allAccountConnectionsServicesIds = allAccountConnections.map(({ service }) => service);
	const uniqueAccountConnectionsServicesIds = [...new Set(allAccountConnectionsServicesIds)]; //remove duplicates from array
	const uniqueAccountConnectionsServices = uniqueAccountConnectionsServicesIds.map((id) => getService(id));
	return uniqueAccountConnectionsServices.map((service) => buildPlacementAddOnItem(service));
};

const formatFavoriteDirectoryAppsToPlacementAddOns = (favoriteDirectoryApps, getService) => {
	let merged = [];
	favoriteDirectoryApps.forEach((directoryApp) => {
		const plyRelatedApps = directoryApp.related_services.map((id) => getService(id));
		plyRelatedApps.forEach((service) => {
			const newMenuItem = buildPlacementAddOnItem(service);
			merged = [...merged, newMenuItem];
		});
	});
	return merged;
};

const convertServiceWorkspaceIdToInternalValue = (serviceWorkspaceId) => {
	if (serviceWorkspaceId === TRANSLATED_UNIDENTIFIED_SERVICE_WORKSPACE_ID) {
		return UNIDENTIFIED_SERVICE_WORKSPACE_ID;
	}
	return serviceWorkspaceId;
};

const convertServiceWorkspaceIdToDisplayValue = (serviceWorkspaceId) => {
	if (serviceWorkspaceId === UNIDENTIFIED_SERVICE_WORKSPACE_ID) {
		return TRANSLATED_UNIDENTIFIED_SERVICE_WORKSPACE_ID;
	}
	return serviceWorkspaceId;
};

const getItemNavigateTo = (item) => {
	if (item.navigateTo) {
		return item.navigateTo;
	} else if (item.workspaceId) {
		return {
			name: PLACEMENT_ADD_ONS_MODE_NAME,
			params: {
				serviceSlug: item.slug,
				serviceWorkspaceId: convertServiceWorkspaceIdToDisplayValue(item.workspaceId), //DISPLAY ONLY - make unidentified magic string, a readable url and text to the user
			},
		};
	} else {
		return { name: PLACEMENT_FEATURE_TYPES_MODE_NAME, params: { featureType: item.slug } };
	}
};

const getPossibleRouteNamesWithPrefix = (routeName) => [
	routeName,
	PLACEMENT_ADD_ONS_MODE_NAME + routeName,
	PLACEMENT_MODE_EVERYTHING_NAME + routeName,
];

// PUBLIC
const isRouteOrChildOfRoute = (currentRoute, routeName) =>
	getPossibleRouteNamesWithPrefix(routeName).includes(currentRoute.name) ||
	currentRoute.matched.some((r) => getPossibleRouteNamesWithPrefix(routeName).includes(r.name));

const doesServiceHaveContext = (service) => service?.has_context;

const getPlacementAddOnsItems = (useAppsWorkspaces, userFavoriteApps, allAccountConnections, getService) => {
	const mergedAppsWorkspacesDirectoryAppsAndConnectionsItems = [
		...formatAppsWorkspacesToPlacementsAddOns(useAppsWorkspaces, getService),
		...formatAllAccountConnectionsToPlacementAddOn(allAccountConnections, getService),
		...formatFavoriteDirectoryAppsToPlacementAddOns(userFavoriteApps, getService),
	].filter((item) => item); //make sure no nulls here
	return removePlacementAddOnsItemsDuplicates(mergedAppsWorkspacesDirectoryAppsAndConnectionsItems);
};

const isPlacementMode = (currentRoute) => isRouteOrChildOfRoute(currentRoute, PLACEMENT_MODE_NAME);
const isPlacementEverythingMode = (currentRoute) => isRouteOrChildOfRoute(currentRoute, PLACEMENT_MODE_EVERYTHING_NAME);
const isPlacementAddOnMode = (currentRoute) => isRouteOrChildOfRoute(currentRoute, PLACEMENT_ADD_ONS_MODE_NAME);

const getPlacementRoutePrefix = (currentRoute) => {
	if (!isPlacementMode(currentRoute)) {
		return '';
	}
	return getPlacementModeRoutePrefix(currentRoute);
};

const getPlacementModeRoutePrefix = (currentRoute) => {
	if (isPlacementAddOnMode(currentRoute)) {
		return PLACEMENT_ADD_ONS_MODE_NAME;
	} else if (isPlacementEverythingMode(currentRoute)) {
		return PLACEMENT_MODE_EVERYTHING_NAME;
	} else {
		return PLACEMENT_FEATURE_TYPES_MODE_NAME;
	}
};

const getAllCalculatedDirectoryApps = (
	allUserAppsWorkspacesServiceDirectoriesIds,
	allUserConnectionsServiceDirectoriesIds,
	userFavoriteAppIds,
	useDirectoryApp
) => {
	const mergedItems = [
		...populateServiceDirectoryIdsWithProps(
			allUserAppsWorkspacesServiceDirectoriesIds,
			CANT_DELETE_BECAUSE_SERVICE_WORKSPACE_KEY,
			useDirectoryApp
		),
		...populateServiceDirectoryIdsWithProps(allUserConnectionsServiceDirectoriesIds, CANT_DELETE_BECAUSE_CONNECTIONS_KEY, useDirectoryApp),
		...populateServiceDirectoryIdsWithProps(userFavoriteAppIds, null, useDirectoryApp),
	];
	return removeDuplicateItemsInArrayByProperties(mergedItems, ['id']).filter((item) => item?.id);
};

const getItemProps = (item) => ({
	icon: item.slug,
	title: item.title,
	navigateTo: getItemNavigateTo(item),
	additionalInfo: item.additionalInfo,
});

export default {
	getItemProps,
	isPlacementMode,
	isPlacementAddOnMode,
	isRouteOrChildOfRoute,
	doesServiceHaveContext,
	getPlacementRoutePrefix,
	getPlacementAddOnsItems,
	isPlacementEverythingMode,
	getPlacementModeRoutePrefix,
	getAllCalculatedDirectoryApps,
	convertServiceWorkspaceIdToInternalValue,
	convertServiceWorkspaceIdToDisplayValue,
};
